import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Row, Col, Card, CardBody, Alert, Button, Spinner,
  FormGroup, Label, Input, Form
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { config } from '../../config/environment';

const validationSchema = Yup.object({
  name: Yup.string().required('Organization name is required'),
  subdomain: Yup.string()
    .required('Subdomain is required')
    .matches(/^[a-z0-9-]+$/, 'Subdomain can only contain lowercase letters, numbers, and hyphens'),
  timezone: Yup.string().required('Timezone is required'),
  address: Yup.string().required('Address is required'),
  phone: Yup.string().required('Phone number is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  gst: Yup.string().optional(),
});

// Timezone option interface
interface TimezoneOption {
  value: string;
  label: string;
  currentTime: string;
  sortKey: number;
}

// Comprehensive timezone list with proper formatting
const getTimezoneOptions = (): TimezoneOption[] => {
  try {
    // Get all supported timezones
    const timezones = (Intl as any).supportedValuesOf ? 
      (Intl as any).supportedValuesOf("timeZone") : 
      [
        // Fallback list of major timezones if Intl.supportedValuesOf is not available
        'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
        'America/Toronto', 'America/Vancouver', 'America/Sao_Paulo', 'America/Mexico_City',
        'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
        'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Zurich', 'Europe/Moscow',
        'Asia/Tokyo', 'Asia/Shanghai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Dubai',
        'Asia/Seoul', 'Asia/Bangkok', 'Asia/Hong_Kong', 'Asia/Manila', 'Asia/Jakarta',
        'Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth', 'Pacific/Auckland',
        'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi'
      ];

    return timezones.map((timezone: string) => {
      try {
        const now = new Date();
        
        // Get current time in this timezone
        const currentTime = now.toLocaleTimeString("en-US", {
          timeZone: timezone,
          hour12: true,
          hour: "numeric",
          minute: "2-digit",
        });
        
        // Get UTC offset using Intl.DateTimeFormat
        const formatter = new Intl.DateTimeFormat('en', {
          timeZone: timezone,
          timeZoneName: 'longOffset'
        });
        const offsetString = formatter.formatToParts(now).find(part => part.type === 'timeZoneName')?.value || 'GMT+00:00';
        
        // Extract the GMT offset from the formatted string (e.g., "GMT+05:30")
        const gmtMatch = offsetString.match(/GMT([+-])(\d{2}):(\d{2})/);
        const gmtOffset = gmtMatch ? `GMT${gmtMatch[1]}${gmtMatch[2]}:${gmtMatch[3]}` : 'GMT+00:00';
        
        // For sorting, convert to minutes
        const tzOffsetMinutes = gmtMatch ? 
          (gmtMatch[1] === '+' ? 1 : -1) * (parseInt(gmtMatch[2]) * 60 + parseInt(gmtMatch[3])) : 0;
        
        // Format timezone name for display
        const displayName = timezone.replace(/_/g, ' ').split('/').pop();
        const region = timezone.split('/')[0];
        const fullDisplayName = region === timezone ? timezone : `${displayName} (${region})`;
        
        return {
          value: timezone,
          label: `(${gmtOffset}) ${fullDisplayName}`,
          currentTime: currentTime,
          sortKey: tzOffsetMinutes
        };
      } catch (error) {
        // Fallback for any timezone that causes issues
        return {
          value: timezone,
          label: timezone,
          currentTime: '',
          sortKey: 0
        };
      }
    })
    .filter((tz: TimezoneOption) => tz.label) // Remove any failed timezone parsing
    .sort((a: TimezoneOption, b: TimezoneOption) => a.sortKey - b.sortKey); // Sort by UTC offset
  } catch (error) {
    // Ultimate fallback
    return [
      { value: 'UTC', label: '(GMT+00:00) UTC', currentTime: '', sortKey: 0 },
      { value: 'America/New_York', label: '(GMT-05:00) New York (America)', currentTime: '', sortKey: -300 },
      { value: 'Europe/London', label: '(GMT+00:00) London (Europe)', currentTime: '', sortKey: 0 },
      { value: 'Asia/Kolkata', label: '(GMT+05:30) Kolkata (Asia)', currentTime: '', sortKey: 330 },
    ];
  }
};

const CreateOrganization: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [flowData, setFlowData] = useState<any>(null);
  const [subdomainValidated, setSubdomainValidated] = useState(false);
  const [lastValidatedSubdomain, setLastValidatedSubdomain] = useState('');
  const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOption[]>([]);

  const checkOnboardingStatus = useCallback(async (onboardingFlowId: string) => {
    try {
      const response = await fetch(config.api.onboardingFlowById(onboardingFlowId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const flow = await response.json();
        console.log('📊 Current flow status:', flow.status);
        
        // Store flow data for form pre-filling
        setFlowData(flow);
        
        // Only allow access if payment is completed but org not yet created
        if (flow.status !== 'PAID') {
          if (flow.status === 'CONFIGURING_STRUCTURE') {
            navigate(`/configure-structure?flow=${onboardingFlowId}`);
          } else if (flow.status === 'CUSTOMIZING_FORMS') {
            navigate(`/customize-forms?flow=${onboardingFlowId}`);
          } else if (flow.status === 'COMPLETE') {
            navigate(`/complete?flow=${onboardingFlowId}`);
          } else {
            navigate(`/getting-started?flow=${onboardingFlowId}`);
          }
          return;
        }
      } else {
        console.error('Failed to fetch onboarding flow status');
        navigate(`/getting-started?flow=${onboardingFlowId}`);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      navigate(`/getting-started?flow=${onboardingFlowId}`);
    }
  }, [navigate]);

  // Check if user has access to this page
  useEffect(() => {
    const flowId = searchParams.get('flow') || searchParams.get('session') || searchParams.get('flowId');
    let onboardingFlowId = flowId || localStorage.getItem('onboardingFlowId');
    
    if (flowId) {
      console.log('🔄 CreateOrganization - Flow ID from URL:', flowId);
      localStorage.setItem('onboardingFlowId', flowId);
      onboardingFlowId = flowId;
    }
    
    if (!onboardingFlowId) {
      navigate('/getting-started');
      return;
    }
    
    checkOnboardingStatus(onboardingFlowId);
  }, [navigate, searchParams, checkOnboardingStatus]);

  // Load timezone options on component mount
  useEffect(() => {
    const options = getTimezoneOptions();
    setTimezoneOptions(options);
  }, []);

  const checkSubdomainAvailability = async () => {
    if (!formik.values.subdomain) {
      setError('Please enter a subdomain first');
      return;
    }

    try {
      const response = await fetch(config.api.validateSubdomain(formik.values.subdomain), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.available) {
        setSuccess('Subdomain is available!');
        setError('');
        setSubdomainValidated(true);
        setLastValidatedSubdomain(formik.values.subdomain);
      } else {
        setError(`Subdomain is not available. Try: ${result.suggestion}`);
        setSuccess('');
        setSubdomainValidated(false);
        setLastValidatedSubdomain('');
      }
    } catch (error) {
      setError('Error checking subdomain availability');
      setSubdomainValidated(false);
      setLastValidatedSubdomain('');
    }
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      subdomain: '',
      address: '',
      phone: '',
      email: '',
      gst: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError('');
      setSuccess('');
      
      try {
        const onboardingFlowId = localStorage.getItem('onboardingFlowId');
        
        if (!onboardingFlowId) {
          setError('No onboarding flow found. Please start over.');
          return;
        }
        
        // Check if subdomain needs validation
        if (!subdomainValidated || lastValidatedSubdomain !== values.subdomain) {
          // Validate subdomain availability before creating organization
          const subdomainResponse = await fetch(config.api.validateSubdomain(values.subdomain), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          const subdomainResult = await subdomainResponse.json();
          if (!subdomainResult.available) {
            setError(`Subdomain "${values.subdomain}" is not available. Try: ${subdomainResult.suggestion}`);
            return;
          }
          
          // Update validation state
          setSubdomainValidated(true);
          setLastValidatedSubdomain(values.subdomain);
        }
        
        // Create organization with basic info only
        const orgResponse = await fetch(config.api.orgSetup(onboardingFlowId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: values.name,
            subdomain: values.subdomain,
            address: values.address,
            phone: values.phone,
            email: values.email,
            gst: values.gst || undefined,
            timezone: values.timezone,
          }),
        });

        if (orgResponse.ok) {
          setSuccess('Organization created successfully! Proceeding to structure configuration...');
          setTimeout(() => {
            const currentFlowId = localStorage.getItem('onboardingFlowId');
            const navigateUrl = currentFlowId ? `/configure-structure?flow=${currentFlowId}` : '/configure-structure';
            navigate(navigateUrl);
          }, 2000);
        } else {
          const errorData = await orgResponse.json();
          setError(errorData.message || 'Failed to create organization');
        }
      } catch (error) {
        console.error('Error creating organization:', error);
        setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    },
  });

  // Update form values when flow data is loaded
  useEffect(() => {
    if (flowData?.organizationName) {
      // Only set if the field is empty to avoid overwriting user input
      if (!formik.values.name) {
        formik.setFieldValue('name', flowData.organizationName);
      }
      
      // Auto-generate subdomain suggestion from organization name
      if (!formik.values.subdomain) {
        const subdomain = flowData.organizationName
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Remove special characters
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .substring(0, 30); // Limit length
        
        if (subdomain) {
          formik.setFieldValue('subdomain', subdomain);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowData]);

  // Reset validation when subdomain changes
  useEffect(() => {
    if (formik.values.subdomain !== lastValidatedSubdomain) {
      setSubdomainValidated(false);
      setSuccess(''); // Clear success message when subdomain changes
    }
  }, [formik.values.subdomain, lastValidatedSubdomain]);

  return (
    <div className="container-fluid" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="row justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="col-md-6 col-lg-5">
          <div className="text-center mb-5">
            <div className="mb-3">
              <div 
                style={{
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: '1.5rem',
                  color: 'white'
                }}
              >
                🏢
              </div>
            </div>
            <h4 className="mb-3">Create Your Organization</h4>
            <p className="text-muted">
              Let's start with the basics. Enter your organization information to create your Mentorgain workspace.
            </p>
          </div>

          {error && <Alert color="danger">{error}</Alert>}
          {success && <Alert color="success">{success}</Alert>}

          <Card className="shadow-sm border-0">
            <CardBody className="p-5">
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label for="name">Organization Name *</Label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Enter organization name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.name && !!formik.errors.name}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <div className="invalid-feedback">{formik.errors.name}</div>
                      )}
                      {flowData?.organizationName && formik.values.name === flowData.organizationName && (
                        <div className="form-text text-muted">
                          <small>✓ Pre-filled from your registration</small>
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label for="subdomain">Subdomain *</Label>
                      <div className="input-group">
                        <Input
                          type="text"
                          id="subdomain"
                          name="subdomain"
                          placeholder="your-org"
                          value={formik.values.subdomain}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.subdomain && !!formik.errors.subdomain}
                        />
                        <span className="input-group-text">.mentorgain.com</span>
                        <Button 
                          type="button" 
                          color={subdomainValidated && lastValidatedSubdomain === formik.values.subdomain ? "success" : "outline-secondary"}
                          onClick={checkSubdomainAvailability}
                        >
                          {subdomainValidated && lastValidatedSubdomain === formik.values.subdomain ? '✓ Verified' : 'Check'}
                        </Button>
                      </div>
                      {formik.touched.subdomain && formik.errors.subdomain && (
                        <div className="invalid-feedback">{formik.errors.subdomain}</div>
                      )}
                      {subdomainValidated && lastValidatedSubdomain === formik.values.subdomain && (
                        <div className="form-text text-success">
                          <small>✓ Subdomain is available</small>
                        </div>
                      )}
                      {formik.values.subdomain && formik.values.subdomain !== lastValidatedSubdomain && !subdomainValidated && (
                        <div className="form-text text-warning">
                          <small>⚠️ Please verify subdomain availability</small>
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label for="timezone">Timezone *</Label>
                      <Input
                        type="select"
                        id="timezone"
                        name="timezone"
                        value={formik.values.timezone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.timezone && !!formik.errors.timezone}
                      >
                        {timezoneOptions.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </Input>
                      {formik.touched.timezone && formik.errors.timezone && (
                        <div className="invalid-feedback">{formik.errors.timezone}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12" md="6">
                    <FormGroup>
                      <Label for="email">Organization Email *</Label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="contact@yourcompany.com"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.email && !!formik.errors.email}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="invalid-feedback">{formik.errors.email}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col xs="12" md="6">
                    <FormGroup>
                      <Label for="phone">Phone Number *</Label>
                      <Input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder="+1 (555) 123-4567"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.phone && !!formik.errors.phone}
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <div className="invalid-feedback">{formik.errors.phone}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <Label for="address">Address *</Label>
                      <Input
                        type="textarea"
                        id="address"
                        name="address"
                        rows={3}
                        placeholder="Enter your organization address"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.address && !!formik.errors.address}
                      />
                      {formik.touched.address && formik.errors.address && (
                        <div className="invalid-feedback">{formik.errors.address}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col xs="12" md="6">
                    <FormGroup>
                      <Label for="gst">GST Number (Optional)</Label>
                      <Input
                        type="text"
                        id="gst"
                        name="gst"
                        placeholder="GST123456789"
                        value={formik.values.gst}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <div className="form-text text-muted">
                        <small>Tax identification number if applicable</small>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end pt-3">
                  <Button 
                    type="submit" 
                    color="primary" 
                    size="lg"
                    disabled={loading || !formik.isValid}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : null}
                    Continue to Structure Setup
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;